import { DocumentPlusIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { DiscriminatedArtifactCreatedEvent } from '@/services/api/generated/matterChangesService';

import {
  ActivityBody,
  ActivityDescription,
  ActivityIcon,
  ActivityItem,
  ActivityTitle,
} from '../activity-feed-v2';
import { getUserFromEvent } from '../activity-feed-v2/ActivityDiffList';
import { formatEnumValue, truncate } from '../activity-feed-v2/utils';

export interface ArtifactCreatedEventProps {
  event: DiscriminatedArtifactCreatedEvent;
}

export const ArtifactCreatedEvent: React.FC<ArtifactCreatedEventProps> = ({
  event,
}) => {
  const { artifact } = event.payload;
  const user = getUserFromEvent(event);

  const getArtifactTypeColor = (type: string) => {
    switch (type) {
      case 'DOCUMENT':
        return 'bg-blue-100 text-blue-600';
      case 'MESSAGE':
      case 'EMAIL':
        return 'bg-purple-100 text-purple-600';
      case 'TRANSCRIPT':
      case 'AUDIO_CONVERSATION':
        return 'bg-green-100 text-green-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const icon = (
    <ActivityIcon
      icon={
        <div
          className={`flex h-6 w-6 items-center justify-center rounded-full ${getArtifactTypeColor(artifact.artifact_type)}`}
        >
          <DocumentPlusIcon className="h-4 w-4 text-current" />
        </div>
      }
    />
  );

  const body = (
    <ActivityBody>
      <ActivityTitle>{artifact.name}</ActivityTitle>
      {artifact.artifact_digest?.one_line_summary && (
        <ActivityDescription>
          <div className="text-xs text-gray-600 italic">
            {truncate(artifact.artifact_digest.one_line_summary, 120)}
          </div>
        </ActivityDescription>
      )}
    </ActivityBody>
  );

  return (
    <ActivityItem
      icon={icon}
      title={`${formatEnumValue(artifact.artifact_type)} Created`}
      timestamp={new Date(event.occurred_at ?? new Date())}
      user={user}
      body={body}
    />
  );
};
